import { useEffect } from "react";

import { useLocation } from "react-router-dom";
import { Global } from "@emotion/react";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import PrintIcon from "@mui/icons-material/Print";
import TvOffIcon from "@mui/icons-material/TvOff";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";

import { useDarkThemeCheck } from "../../../Components/hooks/useDarkThemeCheck";
import { useTvMode } from "../../../Context/TvModeContext";
import getLogo from "../../../utils/logos/logo";
import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { CloudConnect } from "../CloudConnect";
import { KeyContactsSection } from "../KeyContactsSection/KeyContactsSection";
import QuickLinks from "../QuickLinks";
import { useAmountInCustomerCurrency } from "./AmountInCustomerCurrency";
import { ActiveAnomaliesList } from "./CardLists/ActiveAnomaliesList";
import { ActiveBudgetsList } from "./CardLists/ActiveBudgetsList";
import { ActiveInsightsList } from "./CardLists/ActiveInsightsList";
import { CloudWasteCard } from "./Cards/CloudWasteCard";
import { CurrentMonthSpendCard } from "./Cards/CurrentMonthSpendCard";
import { ForecastSpendCard } from "./Cards/ForecastSpendCard";
import { InsightsCard } from "./Cards/InsightsCard";
import { LastMonthSavingsCard } from "./Cards/LastMonthSavingsCard";
import { RecentInquiriesCard } from "./Cards/RecentInquiriesCard";
import { SecurityInsightsCard } from "./Cards/SecurityInsightsCard";
import { CloudCostsSection } from "./CloudCostsSection";
import { useActiveBudgets, useActiveCommitments, useAnomalies, useInsightsPotentialSavings } from "./hooks";
import { printStyles } from "./PrintStyles";

const NewHome = () => {
  const { tvMode, toggleTvMode } = useTvMode();
  const { trackEvent } = useSegmentTrackEvent();

  const location = useLocation();
  const fromPath = (location.state as { from?: string })?.from;

  // Track dashboard viewed event on component mount
  useEffect(() => {
    trackEvent("Dashboard Viewed - Overview", {
      source: "Home Dashboard",
      feature: "Home Dashboard",
      previous_internal_page_route: fromPath,
    });
  }, [trackEvent, fromPath, location]);

  const handlePrint = () => {
    window.print();
  };

  const {
    totalCostAtRisk: anomaliesAtRisk,
    loading: anomaliesLoading,
    activeAnomalies,
    allAnomalies,
    numberOfAnomaliesInLast30Days,
    anomaliesCostAtRiskInLast7Days,
  } = useAnomalies();

  const {
    totalCostAtRisk: budgetsAtRisk,
    loading: budgetsLoading,
    budgets,
    budgetsNearThreshold,
    budgetsCostAtRiskInLast7Days,
  } = useActiveBudgets();
  const {
    totalPotentialSavings: insightsRisk,
    isLoading: insightsLoading,
    cloudFlowsCount,
    cloudFlowsRisk,
    insightsCostAtRiskInLast7Days,
    allInsights,
    securityInsights,
  } = useInsightsPotentialSavings();
  const {
    commitmentsCostAtRisk,
    loading: commitmentsLoading,
    activeCommitmentsAtRiskCount,
    totalCommitment,
    totalAmountOfActiveCommitments,
  } = useActiveCommitments();
  const { logo, logoWhite } = getLogo();

  const isDarkMode = useDarkThemeCheck();

  const budgetsAtRiskInCustomerCurrency = useAmountInCustomerCurrency(budgetsCostAtRiskInLast7Days);

  const costAtRiskInLast7Days =
    budgetsAtRiskInCustomerCurrency + anomaliesCostAtRiskInLast7Days + insightsCostAtRiskInLast7Days;

  return (
    <>
      <Global styles={printStyles} />
      <Box sx={{ mt: -2 }}>
        {tvMode && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -3,
              "@media print": {
                display: "none",
              },
            }}
          >
            <img src={isDarkMode ? logo : logoWhite} alt="DoiT Logo" style={{ width: 78, height: 24 }} />
          </Box>
        )}

        <Box
          sx={{
            "@media print": {
              display: "none",
            },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Tooltip title="Print" arrow>
              <IconButton data-cy="print-btn" sx={{ mr: 0.5 }} onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={tvMode ? "Exit TV mode" : "Enter TV mode"} arrow>
              <IconButton
                data-cy="tv-mode-btn"
                onClick={(e) => {
                  toggleTvMode();
                  e.currentTarget.blur();
                }}
              >
                {tvMode ? <TvOffIcon /> : <PersonalVideoIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Grid container spacing={2} mb={3} id="print-content">
          <Grid size={{ xs: 12, lg: 3 }}>
            <CurrentMonthSpendCard />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <ForecastSpendCard />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <InsightsCard insightsRisk={insightsRisk} loading={insightsLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <SecurityInsightsCard securityInsights={securityInsights} loading={insightsLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <CloudWasteCard
              anomaliesAtRisk={anomaliesAtRisk}
              budgetsAtRisk={budgetsAtRisk}
              insightsRisk={insightsRisk}
              commitmentsAtRisk={commitmentsCostAtRisk}
              anomaliesLoading={anomaliesLoading}
              budgetsLoading={budgetsLoading}
              insightsLoading={insightsLoading}
              commitmentsLoading={commitmentsLoading}
              costAtRiskInLast7Days={costAtRiskInLast7Days}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <LastMonthSavingsCard />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }} sx={{ "@media print": { display: "none" } }}>
            <RecentInquiriesCard />
          </Grid>
          <CloudCostsSection
            cloudFlowsCount={cloudFlowsCount}
            cloudFlowsRisk={cloudFlowsRisk}
            activeCommitmentsAtRiskCount={activeCommitmentsAtRiskCount}
            commitmentsCostAtRisk={commitmentsCostAtRisk}
            loading={commitmentsLoading}
            anomaliesAtRisk={anomaliesAtRisk}
            numberOfAnomaliesInLast30Days={numberOfAnomaliesInLast30Days ?? 0}
            numberOfAnomalies={activeAnomalies?.length ?? 0}
            numberOfBudgets={budgets?.length ?? 0}
            numberOfBudgetsNearThreshold={budgetsNearThreshold.length ?? 0}
            budgetsAtRisk={budgetsAtRisk}
            totalCommitment={totalCommitment}
            totalAmountOfActiveCommitments={totalAmountOfActiveCommitments}
          />
          {(insightsLoading || allInsights.length > 0) && (
            <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
              <ActiveInsightsList insights={allInsights} costAtRisk={insightsRisk} loading={insightsLoading} />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }} mt={2}>
            <ActiveAnomaliesList anomalies={allAnomalies ?? []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
            <ActiveBudgetsList budgets={budgets} budgetsAtRisk={budgetsAtRisk} />
          </Grid>
        </Grid>

        <Box
          sx={{
            "@media print": {
              display: "none",
            },
          }}
        >
          <CloudConnect />
        </Box>

        {/* Key contacts section */}
        {!tvMode && (
          <Box
            sx={{
              "@media print": {
                display: "none",
              },
            }}
          >
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid size={{ xs: 12 }}>
                <QuickLinks />
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                <KeyContactsSection />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </>
  );
};

export default NewHome;
